// Builds the save payload for record edit requests.
//
// Two schemes, distinguished by the payload's requestType:
//
// - 'component': every component of the record carries a unique
//   creation_index, so only the components the user actually changed are
//   sent, determined in two steps: (1) candidates are the components whose
//   onChange the user triggered (tracked by the views' marking setter), and
//   (2) each candidate is compared against its state as displayed when the
//   user first touched it, so an edit that was reverted — or was meaningless,
//   like reselecting a dropdown's current value — is dropped. Only the
//   candidates are ever compared — untouched components are never scanned.
//   content keeps its array-of-subarrays shape (one subarray per page in
//   editedPages), it just omits every unchanged component. Components whose
//   values scripts recompute (from vars, flags, other components) are NOT
//   sent: vars and externals ship in full with every save, so script-driven
//   state is derivable and re-derived on the next load. This lets the server
//   merge simultaneous edits to different parts of one page.
//
// - 'page': legacy fallback for records whose components can't be uniquely
//   identified (forms created before creation_index existed) — sends every
//   component of each page the user touched.
//
// A form either predates creation indices entirely or assigns them to every
// component, so the first component tells us which kind of form this is.

export function supportsComponentEditing(components) {
    if (!Array.isArray(components) || components.length === 0) return false;
    const first = components[0];
    return first !== null && first !== undefined &&
        first.creation_index !== undefined && first.creation_index !== null;
}

// Component types whose record-time state is purely a function of vars and
// static design-time config. They can never hold user input, so they are
// never part of a component-based edit request — vars ship in full with
// every save, and their appearance is re-derived from those on load.
const STATIC_TYPES = new Set([
    'primitive/Label',
    'primitive/Box',
    'primitive/List',
    'primitive/Button',
    'primitive/Image'
]);

// The user-editable payload of a component — exactly what an edit request
// exists to persist: table cells and column definitions (column names are
// user-editable) for InputTables, the drawn/captured data plus props for
// Signature/Snapshot, and props (value / selected / ...) for the input
// primitives. Comparing only this keeps revert detection immune to the
// bookkeeping fields script passes rewrite (error/warning/disable, table row
// vars, and anything added later). Components that do qualify are still sent
// whole.
function editableState(c) {
    if (c.type === 'intermediate/InputTable') {
        return JSON.stringify([c.data, c.colData]);
    }
    if (c.type === 'intermediate/Signature' || c.type === 'intermediate/Snapshot') {
        return JSON.stringify([c.data, c.props]);
    }
    return JSON.stringify(normalizeSelected(c.props));
}

// The widgets write props.selected inconsistently — Radio row clicks store a
// number, its clear button '', Drop stores an index string — and stored
// records may carry any of these forms. Normalize to strings so reselecting
// or reverting to the same option compares equal.
function normalizeSelected(props) {
    if (!props || props.selected === undefined) return props;
    const selected = Array.isArray(props.selected)
        ? props.selected.map((s) => String(s))
        : String(props.selected);
    return { ...props, selected };
}

// Stores component k's current editable state as its pre-edit baseline. The
// views call this from their render loops for every component they put on
// screen, and it keeps refreshing until the user first touches the component
// — so the baseline is always exactly what was displayed when the edit
// attempt began (scripts may have rewritten the component since load; those
// rewrites derive from vars and must not count as the user's edit). Once
// touched, the baseline is frozen for revert comparison at save time.
//
// Client-side tracking (touched set and baselines) is keyed by the array
// position k, which is stable for the life of an open record and — unlike
// creation_index, which legacy forms may carry duplicated — cannot collide
// across components. creation_index stays what identifies each sent
// component to the server.
export function refreshBaseline(baselines, editedComponents, k, c) {
    if (!c || STATIC_TYPES.has(c.type)) return;
    if (editedComponents.has(k)) return;
    baselines.set(k, editableState(c));
}

// After a successful save the server matches what was just sent, so the
// pre-edit reference point moves: clear the touched set and re-baseline every
// component at its just-saved state. Without this, a stay-open save followed
// by reverting a component would compare against the pre-save baseline, look
// like "no change", and silently drop the revert. Only call on success — a
// failed save leaves the edits unsaved, so they must stay tracked.
export function resetEditTracking(components, editedComponents, baselines) {
    editedComponents.clear();
    baselines.clear();
    components.forEach((c, k) => {
        if (c && !STATIC_TYPES.has(c.type)) {
            baselines.set(k, editableState(c));
        }
    });
}

// components: current state at save time (after onSave scripts ran).
// editedComponents: Set of array positions the user directly edited
//   (populated by the views' marking setter passed to the runtime widgets).
// baselines: Map of array position -> pre-edit editable state (see
//   refreshBaseline), for dropping meaningless / reverted edits.
// editedPages: user-touched pages, kept as the fallback scheme's source.
export function buildEditPayload(components, editedComponents, baselines, editedPages) {
    if (supportsComponentEditing(components)) {
        // Only the touched, non-static candidates are ever serialized and
        // compared; a candidate without a baseline compares as changed.
        const changed = components.filter((c, k) =>
            !STATIC_TYPES.has(c.type) &&
            editedComponents.has(k) &&
            baselines.get(k) !== editableState(c));
        const pagesSorted = [...new Set(changed.map((c) => c.page))].sort((a, b) => a - b);
        return {
            requestType: 'component',
            editedPages: pagesSorted,
            content: pagesSorted.map((p) => changed.filter((c) => c.page === p).map(
                (c) => {
                    switch (c.type) {
                        case 'primitive/Check':
                        case 'primitive/Drop':
                        case 'primitive/Radio':
                            return {
                                creation_index: c.creation_index,
                                selected: c.props.selected
                            };
                            break;
                        case 'primitive/Input':
                            return {
                                creation_index: c.creation_index,
                                value: c.props.value
                            }; break;
                        case 'intermediate/Signature':
                            return {
                                creation_index: c.creation_index,
                                data: c.props.data
                            }; break;
                        case 'intermediate/Snapshot':
                            return {
                                creation_index: c.creation_index,
                                data: c.data
                            }; break;
                        case 'intermediate/InputTable':
                            let toReturn = {
                                creation_index: c.creation_index,
                                data: c.data,
                                vars: c.vars
                            };

                            if (c.columnNamesEditable) {
                                toReturn.colData = c.colData;
                            }

                            return toReturn;
                            break;
                        default:
                            return c;
                    }
                }
            ))
        };
    }
    const editedPagesSorted = [...editedPages].sort((a, b) => a - b);
    return {
        requestType: 'record'
    };
}


/*
    AIM: only include the relevant information from a component upon editing.
         i.e., no information about color, shape, position, etc. should be included. only value information.
    COMPONENTS:
        - Omitted:
            * primitive/Box
            * primitive/Button
            * primitive/Image
            * primitive/Label
            * primitive/List
        - Included:
            * primitive/Check: self.creation_index, self.props.selected
            * primitive/Drop: self.creation_index, self.props.selected
            * primitive/Input: self.creation_index, self.props.value
            * primitive/Radio: self.creation_index, self.props.selected
            * intermediate/Signature: self.creation_index, self.props.data
            * intermediate/Snapshot: self.creation_index, self.data
            * intermediate/InputTable: self.creation_index, self.data, self.vars, self.columnNamesEditable ? self.colData : None
*/